import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { tl } from "@/lib/i18nFormat";
import { Store } from "@/lib/types";
import { PageHeader, StatusPill, LocalTime, EmptyState, LoadingBlock } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Store as StoreIcon, Radio, ArrowRight, ListChecks } from "lucide-react";
import StoreDay from "@/pages/StoreDay";
import { TaskExecution } from "@/pages/MyWork";

import { useI18n } from "@/context/I18nContext";

function myStoreId(assignments: any[] = []): string | null {
  const s = assignments.find((a) => a.scope_type === "store" && a.scope_id);
  return s ? s.scope_id : null;
}

export default function Today() {
  const {
    t
  } = useI18n();

  const { user, activeMembership, roleCategory, hasPerm, modules } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const canSeeStores = hasPerm("store.view");

  // Store-scoped managers land directly on their store's Shift Command
  const storeScopeId = roleCategory === "store" ? myStoreId(activeMembership?.assignments) : null;

  useEffect(() => {
    if (!canSeeStores || storeScopeId) { setLoading(false); return; }
    api.get("/org/stores")
      .then((r) => setStores(r.data.stores))
      .catch(() => setStores([]))
      .finally(() => setLoading(false));
  }, [canSeeStores, storeScopeId]);

  if (storeScopeId) return <StoreDay storeIdProp={storeScopeId} />;

  const greeting = tl("Welcome, {name}", { name: user?.name?.split(" ")[0] || tl("there") });

  return (
    (<div>
      <PageHeader
        title={roleCategory === "leadership" ? t("Today") : t("Shift Command")}
        subtitle={activeMembership ? `${greeting} · ${activeMembership.tenant_name}` : greeting}
        testId="today-header"
      />
      <div className="mb-6 rounded-lg border border-brand/20 bg-accent/40 p-4" data-testid="operating-loop-note">
        <div className="flex items-center gap-2 text-sm font-semibold text-accent-foreground">
          <Radio size={16} />{" "}{t("The operating loop")}</div>
        <p className="mt-1 text-sm text-accent-foreground/80">{t(
          "Plan → Live Conditions → Management Response → Execution → Result → Handoff. The active store day\n          (Opening, Quick Capture, Midshift, KPI Check, Closing Handoff) becomes available with the Manager Control module."
        )}</p>
      </div>
      {!canSeeStores ? (
        <div className="space-y-6">
          {activeMembership && modules.execute !== false && (
            <div data-testid="today-tasks">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <ListChecks size={15} />{" "}{t("Today's tasks")}</div>
                <Link to="/my-work" className="text-xs text-brand hover:underline" data-testid="today-tasks-viewall">{t("Open My Work")}</Link>
              </div>
              <TaskExecution todayOnly emptyHint="No tasks due at your store today." />
            </div>
          )}
          <EmptyState
            title="Your personal workspace"
            description="Your schedule, targeted communications, and learning will appear here as those modules come online."
          />
        </div>
      ) : loading ? (
        <LoadingBlock />
      ) : stores.length === 0 ? (
        <EmptyState icon={<StoreIcon size={28} />} title="No stores in your scope" />
      ) : (
        <div>
          <div className="mb-2 text-sm font-semibold text-muted-foreground">
            {roleCategory === "leadership" ? t("Stores in your scope") : t("Your store")}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stores.map((s) => (
              <Link key={s.id} to={`/store/${s.id}`} data-testid={`store-card-${s.store_number}`}>
              <Card className="p-4 transition-shadow hover:shadow-md hover:border-brand/40">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-base font-semibold">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{t("Store #")}{s.store_number}</div>
                  </div>
                  <StatusPill status={s.status} testId={`store-status-${s.store_number}`} />
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-sm">
                  <span className="text-muted-foreground" data-testid={`store-localtime-${s.store_number}`}>
                    <LocalTime tz={s.iana_tz} />
                  </span>
                  <span className="flex items-center gap-1 text-xs text-brand">{t("Shift Command")}{" "}<ArrowRight size={13} />
                  </span>
                </div>
              </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>)
  );
}
